export interface ProjectImage {
  src: string;
  alt: string;
  caption?: string;
}

export interface Project {
  slug: string;
  title: string;
  category: string;
  client: string;
  year: string;
  role: string;
  duration: string;
  tags: string[];
  description: string;
  heroImage: string;
  overview: string;
  challenge: string;
  approach: string;
  outcome: string;
  gallery: Array<string | ProjectImage>;
  color: string;
  featured?: boolean;
}
export const projects: Project[] = [{
  slug: 'deepwatch',
  title: 'Deepwatch Security Center',
  category: 'Enterprise Product Design',
  client: 'Deepwatch',
  year: '2024',
  role: 'Product Design Lead',
  duration: 'Apr 2023 — Jul 2024',
  tags: ['Enterprise SaaS', 'Cybersecurity', 'Data Visualization', 'Design Systems'],
  description: 'A single source of truth for enterprise security posture, operational ticketing, endpoint health, and client trust.',
  heroImage: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?q=80&w=2400&auto=format&fit=crop',
  overview: 'Deepwatch is a managed security service provider offering human-driven Managed Detection and Response (MDR) services. It helps enterprise security teams stay ahead of threats, oversee their security posture, and monitor their systems 24/7/365. I joined initially as a Senior Product Designer alongside one other design team member.',
  challenge: 'Reporting was a two-fold problem. Customer Success Managers spent hours curating PowerPoint presentations every time they needed to report back to a customer—demonstrating completed work or critical events that happened while they were away. Customers, on the other hand, could not see “how the sausage is made,” making it harder to trust the data and feel confident they were getting their money’s worth. Work was lost and redone, and there was little accountability around who, what, or where was working on a given ticket.',
  approach: 'The business needed a dashboard: a single source of truth where clients and Deepwatch professionals could monitor security posture, view open, closed, and in-progress tickets, and take relevant action. CISOs and senior client leaders needed to review ongoing work, track ticket status, identify vulnerabilities across the MITRE board, and plan improvements. The Security Center also brought EDR device health and telemetry into view, separating critical issues from trivial information. I began by creating the visual design language, then grew into leading Ticketing, Notifications, Environmental Health, and Endpoint Detection and Response. As Security Center expanded, the design system grew from form elements and messaging to chat, notifications, date pickers, and data visualizations. As I deepened my cybersecurity knowledge, I partnered with leadership, product, and engineering on the roadmap through card-sorting workshops and user interviews.',
  outcome: 'Security Center gave clients and Deepwatch professionals a shared source of truth for meetings and chat threads, helping teams communicate and collaborate around current work. It reduced the time Deepwatch professionals spent preparing reports so they could focus on work that mattered, strengthened client trust through visibility, and its designs helped support a Series B+ funding round.',
  gallery: [],
  color: '#27373C',
  featured: true
}, {
  slug: 'cosmic-print',
  title: 'Cosmic Print',
  category: 'Product Design',
  client: 'Cosmic Print',
  year: '2026',
  role: 'Solo Product Designer',
  duration: 'Pre-launch',
  tags: ['Product Design', 'Design Systems', 'Content Design', 'Information Design'],
  description: 'A synthesized astrology dashboard that turns four traditions into one plain-language reading.',
  heroImage: 'https://cdn.magicpatterns.com/uploads/uf68KUiUXYAM5xDu1UTGEd/Cosmic_Print_Card_thumb.png',
  overview: 'Cosmic Print synthesizes Western, Vedic, Chinese, and numerology readings into one understandable product experience.',
  challenge: 'Turn four complex and often conflicting astrological systems into an accessible, appropriately layered experience.',
  approach: 'Designed three product iterations, a binding design system, plain-language content rules, and a data-backed rarity catalogue.',
  outcome: 'A completed pre-launch design with its catalogue frozen and handoff in progress.',
  gallery: [],
  color: '#6D4A93',
  featured: true
}, {
  slug: 'winnow',
  title: 'Winnow',
  category: 'Product Design',
  client: 'Winnow Digital Marketing',
  year: '2023',
  role: 'Lead Product Designer',
  duration: '14 months',
  tags: ['UI/UX Design', 'Information Architecture', 'MarTech', 'Prototyping'],
  description: 'Evolving an internal MarTech platform from proof of concept into a focused, usable MVP for client and campaign operations.',
  heroImage: 'https://cdn.magicpatterns.com/uploads/teBYN1y2VPvU7cQJmjh61K/image.png',
  overview: 'Winnow Digital Marketing generates high-quality leads through an AI-assisted MarTech platform spanning Facebook, Instagram, Twitter, Amazon, LinkedIn, and Google. I was brought in to evolve the internal Winnow Admin product from proof of concept into an MVP that could support the team’s day-to-day client, campaign, and analytics work.',
  challenge: 'The existing information architecture made navigation difficult: people had to reselect the client they were working with as they moved between pages, while internal and client-facing product surfaces lacked a clear shared structure. The opportunity was to simplify the workflow without introducing feature complexity for its own sake.',
  approach: 'Working with a fellow product designer, I led discovery sessions across teams, documented everyday tasks and pain points, and ran a usability evaluation. We used affinity mapping to prioritize needs, card sorting to simplify the information architecture, and concept mapping to align stakeholders on scope. Low-fidelity wireframes tested the new navigation and client flows before high-fidelity prototypes established a more polished, consistent interface.',
  outcome: 'The redesigned client workflow elevated the active client into a top-level context, so related pages could cascade beneath it instead of forcing repeated selection. The team received interactive InVision prototypes—not just static mockups—giving them a practical handoff they could review, iterate on, and carry into development with confidence.',
  gallery: [
    {
      src: 'https://cdn.magicpatterns.com/uploads/ktBahffBus9bFFE2CJnFmY/winnow-original-app.png',
      alt: 'The original Winnow Admin interface before the redesign',
      caption: 'Existing product — establishing the starting point',
    },
    {
      src: 'https://cdn.magicpatterns.com/uploads/j5FcqzaFRCKAMP8fZhSRiX/winnow-affinity-map.png',
      alt: 'Affinity map organizing Winnow team needs and pain points',
      caption: 'Research — affinity mapping needs and pain points',
    },
    {
      src: 'https://cdn.magicpatterns.com/uploads/qJQv5UXyckp7WdnC9MKbDh/winnow-card-sorting.png',
      alt: 'Card-sorting exercise used to simplify the Winnow information architecture',
      caption: 'Information architecture — card sorting',
    },
    {
      src: 'https://cdn.magicpatterns.com/uploads/p8MoRmQrvJJtXCBfGxaMd1/winnow-sitemap.png',
      alt: 'Winnow concept map showing the proposed page and flow structure',
      caption: 'Planning — concept map and scope',
    },
    {
      src: 'https://cdn.magicpatterns.com/uploads/645S7cXZtieamZKwkd8PRP/wInnow-wire-improvements.png',
      alt: 'Winnow low-fidelity wireframes exploring improved navigation and page structure',
      caption: 'Wireframes — testing structure and navigation',
    },
    {
      src: 'https://cdn.magicpatterns.com/uploads/i33mC1RjE97fmTKsf6xMS4/winnow-wire-highlights2.png',
      alt: 'Animated highlights from the Winnow wireframe iterations',
      caption: 'Wireframe highlights',
    },
    {
      src: 'https://cdn.magicpatterns.com/uploads/rqVNvoQYQxkXNL4W9T2U2Q/winnow-highlight-client-flow.png',
      alt: 'Winnow client workflow showing the active client as persistent context',
      caption: 'Highlight — a clearer client workflow',
    },
    {
      src: 'https://cdn.magicpatterns.com/uploads/vU5K2KoV8RcuuBSiKS6huX/winnow-highlight-client-flow-example.png',
      alt: 'Example of the redesigned Winnow client-context navigation',
      caption: 'Client navigation in context',
    },
    {
      src: 'https://cdn.magicpatterns.com/uploads/b18BPhEBuXVKAa43RTkdS4/winnow-hifi1.png',
      alt: 'High-fidelity Winnow client profile workflow',
      caption: 'High-fidelity prototype — client profile',
    },
    {
      src: 'https://cdn.magicpatterns.com/uploads/1ZPYQoTrXPWA6i84XjRSBe/winnow-hifi2.png',
      alt: 'High-fidelity Winnow post-creation workflow',
      caption: 'High-fidelity prototype — post creation',
    },
    {
      src: 'https://cdn.magicpatterns.com/uploads/hGkRdWpssNgWPfwMNHkoAS/winnow-hifi3.png',
      alt: 'High-fidelity Winnow client analytics workflow',
      caption: 'High-fidelity prototype — client analytics',
    },
  ],
  color: '#E63946',
  featured: true
}];
export function getProjectBySlug(slug: string): Project | undefined {
  return projects.find(p => p.slug === slug);
}
export function getNextProject(slug: string): Project {
  const idx = projects.findIndex(p => p.slug === slug);
  return projects[(idx + 1) % projects.length];
}